import type { Deal } from './types'

export interface PipelinePoint extends Record<string, string | number> {
  count: number
  month: string
  monthKey: string
  value: number
}

export type PipelineTrendDirection = 'down' | 'neutral' | 'up'

export type PipelineWindowKey = 'all' | 'upcoming' | 'next-90'

const monthFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  year: '2-digit',
})

export function parseDealDate(value?: string): Date | null {
  if (!value) return null
  const date = new Date(value.includes('T') ? value : `${value}T00:00:00`)
  return Number.isNaN(date.getTime()) ? null : date
}

export function isDealInPipelineWindow(deal: Deal, windowKey: PipelineWindowKey): boolean {
  const date = parseDealDate(deal.close_of_escrow_date)
  if (!date) return false
  if (windowKey === 'all') return true

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  if (date < today) return false
  if (windowKey === 'upcoming') return true

  const end = new Date(today)
  end.setDate(end.getDate() + 90)
  return date <= end
}

export function getClosingPipeline(deals: Deal[]): PipelinePoint[] {
  const byMonth = new Map<string, PipelinePoint>()

  for (const deal of deals) {
    const date = parseDealDate(deal.close_of_escrow_date)
    if (!date) continue

    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    const current = byMonth.get(monthKey) ?? {
      count: 0,
      month: monthFormatter.format(date),
      monthKey,
      value: 0,
    }

    current.count += 1
    current.value += deal.purchase_price ?? 0
    byMonth.set(monthKey, current)
  }

  return [...byMonth.values()].sort((a, b) => a.monthKey.localeCompare(b.monthKey))
}

export function formatPipelineAxis(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `$${Math.round(value / 1_000)}k`
  return `$${value}`
}

export function getPipelineTrend(points: PipelinePoint[]): { label: string; trend: PipelineTrendDirection } {
  if (points.length < 2) return { label: 'baseline', trend: 'neutral' }

  const previous = points[points.length - 2]?.value ?? 0
  const current = points[points.length - 1]?.value ?? 0
  const delta = current - previous

  if (previous <= 0 || delta === 0) return { label: 'flat', trend: 'neutral' }

  const pct = Math.abs(delta / previous) * 100
  const prefix = delta > 0 ? '+' : '-'

  return {
    label: `${prefix}${pct.toFixed(0)}%`,
    trend: delta > 0 ? 'up' : 'down',
  }
}
